import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router'
import { ArrowLeft, Info, RefreshCw } from 'lucide-react'
import { api } from '@/api/client'
import type {
  Receipt,
  ReceiptArtifact,
  ReceiptItem,
  SupplyCategory,
  UpcomingStay,
} from '@/api/client'
import {
  DeliverySparkline,
  HeatCell,
  HorizonTimeline,
  KpiStrip,
  NeedsCard,
  categoryLabel,
  formatDate,
  houseMatches,
  houseNeeds,
  kpiCounts,
  nextGuests,
  urgencyOrder,
  type HouseNeeds,
  type KpiKey,
} from './supplies-dashboard'
import { HouseSelect, UnmatchedReceipts, useHouses, useReceiptDecision } from './supplies-unmatched'
import { ClassificationNote, UnsettledReceipts } from './supplies-classification'
import { Badge } from '@/components/ui/badge'
import { CONFIDENCE_LABELS, CONFIDENCE_TONES } from '@/lib/supply-confidence'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ErrorState } from '@/components/ui/error-state'
import { TableSkeleton } from '@/components/ui/loading-skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

/**
 * Supplies (CAP-464, v2 in CAP-471; admin-api docs/receipts-supplies-spike.md
 * section 4.6).
 *
 * Three things on one page: what will run out where and when (the forecast,
 * so an order goes out before the house is empty), what each house last
 * received per category and how that is going (the grid), and, for one
 * house, what else should go in the order being placed (planning mode).
 *
 * **Nothing here is computed.** Every date, quantity, runout, confidence and
 * sentence arrives from admin-api's per-guest-night model, the same numbers
 * the MCP tools return, so the page and Claude can never disagree. The only
 * things derived here are labels, grouping by week, and layout.
 */

function money(n: number | null): string {
  return n === null ? '—' : `$${n.toFixed(2)}`
}

export { categoryLabel } from './supplies-dashboard'

export function SuppliesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const propertyId = searchParams.get('property')

  if (propertyId) {
    return (
      <PropertyView propertyId={propertyId} onBack={() => setSearchParams({}, { replace: true })} />
    )
  }
  return <Overview onSelect={id => setSearchParams({ property: id })} />
}

function Overview({ onSelect }: { onSelect: (propertyId: string) => void }) {
  const [openReceipt, setOpenReceipt] = useState<ReceiptTarget | null>(null)
  const [filter, setFilter] = useState<KpiKey | null>(null)
  const overview = useQuery({
    queryKey: ['supplies', 'overview'],
    queryFn: () => api.getSuppliesOverview(),
    staleTime: 60_000,
  })

  const data = overview.data
  const counts = useMemo(() => (data ? kpiCounts(data.properties, data.today) : null), [data])
  const rows = useMemo(() => {
    if (!data) return []
    const list = filter
      ? data.properties.filter(p => houseMatches(p, filter, data.today))
      : data.properties
    return [...list].sort((a, b) => urgencyOrder(a, b, data.today))
  }, [data, filter])

  if (overview.isPending) return <TableSkeleton rows={8} cols={6} />
  const openFromList = (r: { id: string }, house: string) =>
    setOpenReceipt({ id: r.id, category: null, propertyName: house })
  if (overview.isError) {
    // Classification reads its own endpoints, so it stays usable when the forecast is down.
    return (
      <div className="space-y-4">
        <ErrorState
          error={
            overview.error instanceof Error ? overview.error : new Error('Could not load supplies.')
          }
          onRetry={() => overview.refetch()}
        />
        <ReceiptClassificationCard onOpen={openFromList} />
        <ReceiptDialog target={openReceipt} onClose={() => setOpenReceipt(null)} />
      </div>
    )
  }
  const { categories, properties, today, horizon_days } = overview.data
  if (properties.length === 0) {
    // A receipt can be waiting or failed before it adds any supply, so the
    // classification card shows even with nothing to forecast.
    return (
      <div className="space-y-4">
        <EmptyState message="No supply deliveries yet. They appear here once receipts with supplies on them reach admin-api." />
        <ReceiptClassificationCard onOpen={openFromList} />
        <ReceiptDialog target={openReceipt} onClose={() => setOpenReceipt(null)} />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {counts && <KpiStrip counts={counts} active={filter} onSelect={setFilter} />}

      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <SectionTitle title={`Next ${horizon_days} days`}>
              Each bar is a stay with its guest count. The red count next to a house is how many
              supplies are already gone there; an amber dot is the day one is expected to run out.
              Order before a dot meets a bar. Houses with nothing booked and nothing due are counted
              underneath, not drawn.
            </SectionTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => overview.refetch()}
              aria-label="Refresh supplies"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-3">
            <HorizonTimeline
              rows={properties}
              today={today}
              horizonDays={horizon_days}
              onSelect={onSelect}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
            <SectionTitle title="Every house and supply">
              Red is gone, amber runs out inside the next {horizon_days} days, green is fine, grey
              has no trend yet. The bar under a chip is what is left of the last delivery. The × tag
              is how the house uses that supply against the portfolio: red heavier, blue lighter.
              Hover a chip for the reasoning and the order history, click it for the receipt; hover
              Needs for what to order; click a house to plan an order.
            </SectionTitle>
            {categories.length === 0 && (
              <p className="text-xs text-textMuted">
                No consumable deliveries yet, only one-off purchases. Click a house to see them.
              </p>
            )}
            {filter && (
              <button
                type="button"
                onClick={() => setFilter(null)}
                className="text-xs text-mint underline-offset-2 hover:underline focus:outline-hidden focus:ring-2 focus:ring-mint"
              >
                Show all {properties.length} houses
              </button>
            )}
          </div>
          {rows.length === 0 ? (
            <p className="px-4 pb-4 pt-2 text-sm text-textMuted">No house matches that filter.</p>
          ) : (
            <Table className="text-xs" containerClassName="max-h-[calc(100vh-14rem)]">
              <TableHeader className="sticky top-0 z-30 bg-surface2 shadow-[0_1px_0_0_var(--color-border)]">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="sticky left-0 z-40 h-9 min-w-44 bg-surface2 px-3 text-[11px] font-semibold uppercase tracking-wide text-text">
                    House
                  </TableHead>
                  <TableHead className="h-9 min-w-40 whitespace-nowrap px-3 text-[11px] font-semibold uppercase tracking-wide text-text">
                    Needs
                  </TableHead>
                  {categories.map(c => (
                    <TableHead
                      key={c}
                      className="h-9 whitespace-nowrap px-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-textMuted"
                    >
                      {categoryLabel(c)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(p => {
                  const needs = houseNeeds(p, today)
                  return (
                    <TableRow key={p.property_id}>
                      <TableCell className="sticky left-0 z-20 min-w-44 whitespace-nowrap bg-surface px-3 py-1 font-medium">
                        <button
                          type="button"
                          className="rounded-sm text-left text-text hover:text-mint focus:outline-hidden focus:ring-2 focus:ring-mint"
                          onClick={() => onSelect(p.property_id)}
                        >
                          {p.name}
                        </button>
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1 text-textMuted">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              tabIndex={0}
                              role="note"
                              aria-label={`What ${p.name} needs`}
                              className="cursor-default rounded-sm focus:outline-hidden focus:ring-2 focus:ring-mint"
                            >
                              <HouseSummary needs={needs} stays={p.upcoming_stays} today={today} />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="w-80 p-0 text-xs">
                            <NeedsCard row={p} needs={needs} today={today} />
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      {categories.map(c => {
                        const cell = p.categories[c]
                        return (
                          <TableCell key={c} className="px-1 py-1 align-middle">
                            {cell ? (
                              <HeatCell
                                cell={cell}
                                today={today}
                                category={c}
                                house={p.name}
                                portfolio={overview.data.portfolio[c]}
                                onOpen={() =>
                                  setOpenReceipt({
                                    id: cell.last_receipt_id,
                                    category: c,
                                    propertyName: p.name,
                                  })
                                }
                              />
                            ) : (
                              <span
                                className="block text-center text-textFaint"
                                aria-label="no orders"
                              >
                                ·
                              </span>
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <SectionTitle title="Unmatched receipts">
            Deliveries admin-api could not put on a house: no address on the receipt, an address
            that matches no house, or one that fits several. Read the recipient, the address and the
            items, pick the house and match it; the chips of that house update at once and the
            resolver never overrides a match made here. Orders shipped to the home of Andrei or of a
            cleaner are skipped on purpose and only counted.
          </SectionTitle>
          <div className="mt-2">
            <UnmatchedReceipts />
          </div>
        </CardContent>
      </Card>

      <ReceiptClassificationCard onOpen={openFromList} />

      <ReceiptDialog target={openReceipt} onClose={() => setOpenReceipt(null)} />
    </div>
  )
}

/** Receipts waiting for the model or failed (CAP-683), with the re-run. */
function ReceiptClassificationCard({
  onOpen,
}: {
  onOpen: (r: { id: string }, house: string) => void
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <SectionTitle title="Receipt classification">
          Each line on a receipt is sorted into a supply, a one-off or not a supply. Rules and
          earlier answers do most lines at once; the rest wait for the model, which can take a while
          when it is offline. Receipts waiting for the model or failed are listed here; open any
          receipt to see where its classification stands. Re-run classification after a failure,
          once the model is back.
        </SectionTitle>
        <div className="mt-2">
          <UnsettledReceipts onOpen={onOpen} />
        </div>
      </CardContent>
    </Card>
  )
}

/** A card's name with an (i) that holds the legend, instead of a paragraph over the table (Andrei, 2026-09-21). */
function SectionTitle({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <h2 className="text-sm font-semibold text-text">{title}</h2>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About ${title}`}
            className="rounded-full text-textFaint hover:text-textMuted focus:outline-hidden focus:ring-2 focus:ring-mint"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="max-w-sm whitespace-normal p-3 text-xs leading-relaxed"
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

/** The "Needs" column: what to order and who is coming, in five words. */
function HouseSummary({
  needs,
  stays,
  today,
}: {
  needs: HouseNeeds
  stays: UpcomingStay[]
  today: string
}) {
  const due = needs.due_week.length + needs.due_later.length
  return (
    <div className="leading-tight">
      <div>
        {needs.order_now.length > 0 && (
          <span className="font-medium text-danger">{needs.order_now.length} now</span>
        )}
        {needs.order_now.length > 0 && due > 0 && <span className="text-textFaint"> · </span>}
        {due > 0 && <span className="font-medium text-amber">{due} soon</span>}
        {needs.order_now.length === 0 && due === 0 && (
          <span className="text-textFaint">
            {needs.idle_gone.length > 0
              ? `${needs.idle_gone.length} gone, no guests`
              : 'nothing due'}
          </span>
        )}
      </div>
      <div className="text-textFaint">{nextGuests(stays, today)}</div>
    </div>
  )
}

/** `category` is the supply the reader clicked, to show first; null when opened from a list. */
type ReceiptTarget = { id: string; category: string | null; propertyName: string }

const SIZE_UNIT =
  '(?:fl ?oz|oz|lbs?|ct|count|pack|pk|rolls?|loads?|sheets?|gallons?|gal|liters?|ml|l)'
// A size may carry thousands separators ("11,000 sheets"); without the
// group below the token started after the comma and read "000 sheets".
const SIZE_TOKEN = new RegExp(
  `\\b(\\d{1,3}(?:,\\d{3})+|\\d+(?:\\.\\d+)?)[ -]?${SIZE_UNIT}\\b`,
  'gi'
)

/**
 * A retailer's item name is a marketing string ("Dove Body Wash with Pump
 * Restore Coconut & Cocoa Butter for 24hr Lotion-Soft Skin Moisturizing Skin
 * Cleanser with No Sulfates No Parabens 30.6 Oz"). For reading a receipt the
 * product is the part before the first comma, bracket or " for ", and the
 * size is worth keeping on its own. Presentation only: the full name stays
 * in the tooltip and nothing downstream reads the short form.
 */
export function shortItemName(nameRaw: string): { name: string; detail: string | null } {
  const raw = nameRaw.replace(/\s+/g, ' ').trim()
  const sizes = [...new Set([...raw.matchAll(SIZE_TOKEN)].map(m => m[0].toLowerCase()))]
  let head = raw.split(/,| \(| - | – /)[0]
  const forAt = head.search(/ for /i)
  if (forAt > 12) head = head.slice(0, forAt)
  head = head.trim()
  if (head.length > 60) {
    const cut = head.lastIndexOf(' ', 60)
    head = `${head.slice(0, cut > 20 ? cut : 60)}…`
  }
  const headLower = head.toLowerCase()
  const detail = sizes.filter(t => !headLower.includes(t)).join(' · ')
  return { name: head, detail: detail || null }
}

/** "174 FOREST RD" reads as shouting next to a house name; only all-caps strings are recased. */
export function tidyAddress(address: string): string {
  if (address !== address.toUpperCase()) return address
  return address
    .toLowerCase()
    .replace(/\b([a-z])/g, c => c.toUpperCase())
    .replace(/\b(Rd|St|Ave|Ln|Dr|Ct|Blvd|Ma)\b\.?/g, (_, w: string) => (w === 'Ma' ? 'MA' : w))
}

/** Where the retailer shows the order, when the reference is one their site takes. */
export function orderLink(receipt: Pick<Receipt, 'source' | 'order_ref'>): string | null {
  if (receipt.source === 'amazon' && /^\d{3}-\d{7}-\d{7}$/.test(receipt.order_ref)) {
    return `https://www.amazon.com/gp/your-account/order-details?orderID=${receipt.order_ref}`
  }
  return null
}

/** What grouping reads off a line; a full ReceiptItem is one. */
type GroupableItem = Pick<ReceiptItem, 'category'> & { kind: string | null }
type ReceiptGroup<T extends GroupableItem = ReceiptItem> = {
  key: string
  label: string
  items: T[]
  tracked: boolean
}

/**
 * Lines grouped by what they are, the category the reader clicked first,
 * then the other supplies in receipt order, then one-offs, then anything the
 * classifier left out, so "what else came that day" is answered by scanning
 * headings rather than product names.
 */
export function groupReceiptItems<T extends GroupableItem>(
  items: T[],
  focus: string | null
): ReceiptGroup<T>[] {
  const groups = new Map<string, ReceiptGroup<T>>()
  const keyOf = (item: T): ReceiptGroup<T> => {
    if (item.kind === 'consumable' && item.category) {
      return { key: item.category, label: categoryLabel(item.category), items: [], tracked: true }
    }
    if (item.kind === 'one_off') {
      return { key: 'one_off', label: 'One-off, not tracked', items: [], tracked: false }
    }
    if (item.kind === 'excluded') {
      return { key: 'excluded', label: 'Not a supply', items: [], tracked: false }
    }
    return { key: 'unclassified', label: 'Not classified yet', items: [], tracked: false }
  }
  for (const item of items) {
    const g = keyOf(item)
    const existing = groups.get(g.key)
    if (existing) existing.items.push(item)
    else groups.set(g.key, { ...g, items: [item] })
  }
  const order = (g: ReceiptGroup<T>) =>
    g.key === focus ? 0 : g.tracked ? 1 : g.key === 'one_off' ? 2 : g.key === 'excluded' ? 3 : 4
  return [...groups.values()].sort((a, b) => order(a) - order(b))
}

/** "payload.json · 2 KB", and a rebuilt payload says so, since it is not what the script sent. */
export function artifactLabel(a: Pick<ReceiptArtifact, 'name' | 'bytes' | 'origin'>): string {
  const size = a.bytes >= 1024 ? `${Math.round(a.bytes / 1024)} KB` : `${a.bytes} B`
  return a.origin === 'seeded_from_d1'
    ? `${a.name} · ${size} · rebuilt from the rows`
    : `${a.name} · ${size}`
}

/** Sum of the parts the receipt itemises; when it is not the total, coupons or adjustments made up the rest. */
export function itemisedParts(r: Receipt): number | null {
  if (r.subtotal === null) return null
  return r.subtotal + (r.tax ?? 0) + (r.tip ?? 0) + (r.fees ?? 0)
}

function isWaiting(data: { receipt: Receipt } | undefined): boolean {
  return data?.receipt.classification_status === 'pending'
}

/** The receipt behind a delivery: every line on it, so "what else came that day" is one click. */
function ReceiptDialog({ target, onClose }: { target: ReceiptTarget | null; onClose: () => void }) {
  const receipt = useQuery({
    queryKey: ['receipt', target?.id],
    queryFn: () => api.getReceipt(target!.id),
    enabled: target !== null,
    // A receipt waiting for the model can settle any minute: never serve it
    // from cache, and keep it fresh while the dialog stays open (CAP-683).
    staleTime: query => (isWaiting(query.state.data) ? 0 : 5 * 60_000),
    refetchInterval: query => (isWaiting(query.state.data) ? 60_000 : false),
  })
  const r = receipt.data?.receipt
  const groups = useMemo(
    () => (r ? groupReceiptItems(r.items, target?.category ?? null) : []),
    [r, target?.category]
  )
  const link = r ? orderLink(r) : null
  const parts = r ? itemisedParts(r) : null
  return (
    <Dialog open={target !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <p className="text-xs font-semibold uppercase tracking-wide text-textFaint">
            {target?.propertyName}
          </p>
          <DialogTitle>
            {r ? `${r.retailer ?? r.source} · ${formatDate(r.ordered_at.slice(0, 10))}` : 'Receipt'}
          </DialogTitle>
          <DialogDescription>
            {r ? (
              <>
                {link ? (
                  <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-mint underline-offset-2 hover:underline"
                  >
                    Order {r.order_ref}
                  </a>
                ) : r.order_ref.startsWith('sheet-') ? (
                  'From the receipts sheet'
                ) : (
                  `Order ${r.order_ref}`
                )}
                {r.delivery_recipient || r.delivery_address ? ' · shipped to ' : null}
                {r.delivery_recipient}
                {r.delivery_recipient && r.delivery_address ? ', ' : null}
                {r.delivery_address ? tidyAddress(r.delivery_address) : null}
              </>
            ) : (
              'Everything that was on this delivery.'
            )}
          </DialogDescription>
        </DialogHeader>
        {receipt.isPending && <p className="text-sm text-textFaint">Loading the receipt…</p>}
        {receipt.isError && <p className="text-sm text-danger">Could not load this receipt.</p>}
        {r && (
          <div className="max-h-[60vh] overflow-auto pr-1">
            <ClassificationNote receipt={r} />
            {groups.map(g => {
              const focused = g.key === target?.category
              const groupTotal = g.items.reduce((sum, i) => sum + (i.line_total ?? 0), 0)
              return (
                <section
                  key={g.key}
                  aria-label={g.label}
                  className={`mb-3 rounded-sm ${focused ? 'border-l-2 border-mint pl-3' : 'pl-3'}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h3
                      className={`text-xs font-semibold uppercase tracking-wide ${
                        focused ? 'text-mint' : g.tracked ? 'text-textMuted' : 'text-textFaint'
                      }`}
                    >
                      {g.label}
                    </h3>
                    <span className="text-xs text-textFaint">{money(groupTotal)}</span>
                  </div>
                  <ul className="mt-1 space-y-1.5">
                    {g.items.map((item, i) => {
                      const short = shortItemName(item.name_raw)
                      return (
                        <li
                          key={i}
                          className="grid grid-cols-[minmax(0,1fr)_5rem] items-baseline gap-x-3 text-sm sm:grid-cols-[minmax(0,1fr)_auto_5rem]"
                        >
                          <span title={item.name_raw}>
                            <span className={g.tracked ? 'text-text' : 'text-textMuted'}>
                              {short.name}
                            </span>
                            {short.detail && (
                              <span className="text-textFaint"> · {short.detail}</span>
                            )}
                          </span>
                          {/* Under the name on a phone, so the name keeps the width. */}
                          <span className="col-start-1 row-start-2 whitespace-nowrap text-xs text-textMuted sm:col-start-auto sm:row-start-auto">
                            {item.qty !== 1 || item.unit_price !== null
                              ? `${item.qty} × ${money(item.unit_price)}`
                              : null}
                          </span>
                          <span className="col-start-2 row-start-1 whitespace-nowrap text-right text-text sm:col-start-auto sm:row-start-auto">
                            {money(item.line_total)}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
            <dl className="ml-auto mt-2 grid w-56 grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 border-t border-border pt-2 text-sm">
              {r.subtotal !== null && (
                <>
                  <dt className="text-textMuted">Items</dt>
                  <dd className="text-right text-text">{money(r.subtotal)}</dd>
                </>
              )}
              {r.tax !== null && r.tax !== 0 && (
                <>
                  <dt className="text-textMuted">Tax</dt>
                  <dd className="text-right text-text">{money(r.tax)}</dd>
                </>
              )}
              {r.tip !== null && r.tip !== 0 && (
                <>
                  <dt className="text-textMuted">Tip</dt>
                  <dd className="text-right text-text">{money(r.tip)}</dd>
                </>
              )}
              {r.fees !== null && r.fees !== 0 && (
                <>
                  <dt className="text-textMuted">Fees</dt>
                  <dd className="text-right text-text">{money(r.fees)}</dd>
                </>
              )}
              <dt className="font-medium text-text">Paid</dt>
              <dd className="text-right font-medium text-text">{money(r.total)}</dd>
            </dl>
            {parts !== null && Math.abs(parts - r.total) >= 0.01 && (
              <p className="mt-1 text-right text-xs text-textFaint">
                Coupons and adjustments are not itemised on this receipt.
              </p>
            )}
            {r.artifacts.length > 0 && (
              <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
                <span className="text-textFaint">Originals</span>
                {r.artifacts.map(a => (
                  <a
                    key={a.name}
                    href={api.receiptArtifactUrl(r.id, a.name)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-mint underline-offset-2 hover:underline focus:outline-hidden focus:ring-2 focus:ring-mint"
                    title={`${a.content_type} · ${a.bytes} bytes`}
                  >
                    {artifactLabel(a)}
                  </a>
                ))}
              </div>
            )}
            {r.raw_text && (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs text-textFaint hover:text-textMuted">
                  Original receipt text
                </summary>
                <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-sm bg-surface2 p-2 text-xs text-textMuted">
                  {r.raw_text}
                </pre>
              </details>
            )}
            <MoveReceipt receipt={r} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Re-pin a receipt the resolver put on the wrong house (CAP-498); the resolver never overrides a pin. */
function MoveReceipt({ receipt: r }: { receipt: Receipt }) {
  const houses = useHouses()
  const [choice, setChoice] = useState('')
  const { match, busy } = useReceiptDecision(r.id, houses)
  const current = houses.find(h => h.id === r.property_id)?.name ?? r.property_id
  const how =
    r.attribution === 'manual'
      ? 'pinned by hand'
      : r.attribution === 'address'
        ? 'matched by its delivery address'
        : r.attribution === 'recipient_name'
          ? 'matched by the name on the delivery'
          : null
  return (
    <form
      className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3 text-xs"
      onSubmit={e => {
        e.preventDefault()
        if (choice) {
          match.mutate(choice, { onSuccess: () => setChoice('') })
        }
      }}
    >
      <span className="w-full text-textMuted">
        {current ? `On ${current}` : 'On no house'}
        {how ? `, ${how}` : ''}.
      </span>
      <HouseSelect
        id={`move-${r.id}`}
        label="Move this receipt to"
        value={choice}
        onChange={setChoice}
        disabled={busy}
        houses={houses}
        exclude={r.property_id}
      />
      <Button type="submit" size="sm" variant="outline" disabled={!choice || busy}>
        {match.isPending ? 'Moving…' : 'Move'}
      </Button>
    </form>
  )
}

function PropertyView({ propertyId, onBack }: { propertyId: string; onBack: () => void }) {
  const [planning, setPlanning] = useState(false)
  const [inCart, setInCart] = useState<string[]>([])
  const [openReceipt, setOpenReceipt] = useState<ReceiptTarget | null>(null)

  const supplies = useQuery({
    queryKey: ['supplies', 'property', propertyId],
    queryFn: () => api.getPropertySupplies(propertyId),
    staleTime: 60_000,
  })
  const restock = useQuery({
    queryKey: ['supplies', 'restock', propertyId, inCart],
    queryFn: () => api.getRestockSuggestions(propertyId, inCart),
    enabled: planning,
    staleTime: 60_000,
  })
  const overview = useQuery({
    queryKey: ['supplies', 'overview'],
    queryFn: () => api.getSuppliesOverview(),
    staleTime: 60_000,
  })
  const propertyName = useMemo(
    () => overview.data?.properties.find(p => p.property_id === propertyId)?.name ?? null,
    [overview.data, propertyId]
  )

  const toggleCart = (category: string) =>
    setInCart(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="Back to all properties">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold text-text">
            {propertyName ?? (
              <Link to={`/properties/${encodeURIComponent(propertyId)}`}>{propertyId}</Link>
            )}
          </h2>
        </div>
        <Button
          variant={planning ? 'default' : 'outline'}
          size="sm"
          onClick={() => setPlanning(v => !v)}
          aria-pressed={planning}
        >
          {planning ? 'Done planning' : 'Plan an order'}
        </Button>
      </div>

      {supplies.isPending && <TableSkeleton rows={6} cols={5} />}
      {supplies.isError && (
        <ErrorState
          error={
            supplies.error instanceof Error ? supplies.error : new Error('Could not load supplies.')
          }
          onRetry={() => supplies.refetch()}
        />
      )}
      {supplies.data && (
        <>
          {planning && (
            <Card>
              <CardContent className="space-y-2 p-4">
                <p className="text-sm text-textMuted">
                  Tick what is already going in the cart. Everything below that is expected to run
                  out within {supplies.data.horizon_days} days of bookings is worth adding now
                  rather than paying another delivery fee later.
                </p>
                {restock.isPending && <p className="text-sm text-textFaint">Checking…</p>}
                {restock.isError && (
                  <p className="text-sm text-danger">Could not load suggestions.</p>
                )}
                {restock.data && (
                  <div>
                    <p className="text-sm font-medium text-text">
                      {restock.data.suggestions.length === 0
                        ? 'Nothing else is due within the horizon.'
                        : 'Also add:'}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {restock.data.suggestions.map(s => (
                        <li key={s.category} className="text-sm text-text">
                          <span className="font-medium">{categoryLabel(s.category)}</span>
                          <span className="text-textMuted"> — {s.reasoning}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {supplies.data.categories.length === 0 ? (
            <EmptyState message="No consumable deliveries on record for this house. Instacart and Amazon deliveries appear here once ingested and classified." />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div>
                  <Table
                    className="min-w-240 text-xs"
                    containerClassName="max-h-[calc(100vh-16rem)]"
                  >
                    <TableHeader className="sticky top-0 z-30 bg-surface2 shadow-[0_1px_0_0_var(--color-border)] [&_th]:h-9 [&_th]:px-3 [&_th]:text-[11px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-text">
                      <TableRow className="hover:bg-transparent">
                        {planning && <TableHead className="w-10">Cart</TableHead>}
                        <TableHead>Category</TableHead>
                        <TableHead>Last delivered</TableHead>
                        <TableHead>Expected</TableHead>
                        <TableHead>Confidence</TableHead>
                        <TableHead>Last 12 months</TableHead>
                        <TableHead>Why</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supplies.data.categories.map(c => (
                        <CategoryRow
                          key={c.category}
                          category={c}
                          today={supplies.data.today}
                          planning={planning}
                          inCart={inCart.includes(c.category)}
                          onToggle={() => toggleCart(c.category)}
                          onOpenReceipt={() =>
                            setOpenReceipt({
                              id: c.last_delivery.receipt_id,
                              category: c.category,
                              propertyName: propertyName ?? propertyId,
                            })
                          }
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {supplies.data.one_offs.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-text">One-off purchases</h3>
                <p className="text-xs text-textMuted">
                  Recorded so &ldquo;when did I buy X for this house&rdquo; is answerable; never
                  predicted.
                </p>
                <ul className="mt-2 space-y-1">
                  {supplies.data.one_offs.map(o => (
                    <li key={`${o.receipt_id}-${o.name}`} className="text-sm text-text">
                      <span className="text-textMuted">{formatDate(o.date)}</span> {o.name}
                      {o.qty > 1 ? ` ×${o.qty}` : ''}
                      {o.retailer ? <span className="text-textFaint"> · {o.retailer}</span> : null}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <ReceiptDialog target={openReceipt} onClose={() => setOpenReceipt(null)} />
    </div>
  )
}

/** "0.05 vs 0.04 portfolio", per guest-night, as units per 100 guest-nights. */
export function rateVsPortfolio(c: SupplyCategory): string {
  if (c.rate === null) return 'no rate yet'
  const per100 = (r: number) => (r * 100).toFixed(1)
  if (c.portfolio_rate === null) return `${per100(c.rate)} per 100 guest-nights`
  const diff = c.property_rate === null ? null : c.property_rate / c.portfolio_rate
  const vs =
    diff === null
      ? 'portfolio rate'
      : diff > 1.25
        ? 'uses more than most'
        : diff < 0.8
          ? 'uses less than most'
          : 'like the portfolio'
  return `${per100(c.rate)} per 100 guest-nights · ${vs}`
}

/** The property view's wording for a category, same rules as the grid cell. */
function categoryExpected(c: SupplyCategory, today: string): string {
  if (c.rate === null) return 'no trend yet'
  if (c.stock_estimate === 0 || (c.runout_date !== null && c.runout_date <= today)) {
    return 'likely gone'
  }
  if (c.due_within_horizon && c.runout_date) return `low by ${formatDate(c.runout_date)}`
  if (c.runout_date) return `fine until ${formatDate(c.runout_date)}`
  return 'fine for now'
}

function CategoryRow({
  category: c,
  today,
  planning,
  inCart,
  onToggle,
  onOpenReceipt,
}: {
  category: SupplyCategory
  today: string
  planning: boolean
  inCart: boolean
  onToggle: () => void
  onOpenReceipt: () => void
}) {
  const [open, setOpen] = useState(false)
  const expected = categoryExpected(c, today)
  return (
    <>
      <TableRow
        className={
          c.due_within_horizon
            ? '[&>td:first-child]:border-l-2 [&>td:first-child]:border-amber'
            : undefined
        }
      >
        {planning && (
          <TableCell className="px-3 py-1.5">
            <input
              type="checkbox"
              aria-label={`${categoryLabel(c.category)} in cart`}
              checked={inCart}
              onChange={onToggle}
              className="h-4 w-4 accent-mint"
            />
          </TableCell>
        )}
        <TableCell className="px-3 py-1.5 font-medium">
          <button
            type="button"
            className="rounded-sm text-left hover:text-mint focus:outline-hidden focus:ring-2 focus:ring-mint"
            onClick={() => setOpen(v => !v)}
            aria-expanded={open}
          >
            {categoryLabel(c.category)}
          </button>
        </TableCell>
        <TableCell className="whitespace-nowrap px-3 py-1.5">
          <button
            type="button"
            onClick={onOpenReceipt}
            className="rounded-sm text-left hover:text-mint focus:outline-hidden focus:ring-2 focus:ring-mint"
            aria-label={`Open the receipt from ${c.last_delivery.date}`}
          >
            {formatDate(c.last_delivery.date)}
            <span className="text-textMuted">
              {' '}
              · {c.last_delivery.qty} {c.last_delivery.qty === 1 ? 'unit' : 'units'}
              {c.last_delivery.retailer ? ` · ${c.last_delivery.retailer}` : ''}
            </span>
          </button>
        </TableCell>
        <TableCell
          className={
            c.due_within_horizon
              ? 'whitespace-nowrap px-3 py-1.5 text-amber'
              : 'whitespace-nowrap px-3 py-1.5'
          }
        >
          {expected}
        </TableCell>
        <TableCell className="whitespace-nowrap px-3 py-1.5">
          <Badge variant={CONFIDENCE_TONES[c.confidence]}>{CONFIDENCE_LABELS[c.confidence]}</Badge>
        </TableCell>
        <TableCell className="whitespace-nowrap px-3 py-1.5">
          <div className="flex flex-col gap-0.5">
            <DeliverySparkline deliveries={c.deliveries} today={today} />
            <span className="text-textMuted">{rateVsPortfolio(c)}</span>
          </div>
        </TableCell>
        <TableCell className="min-w-[20rem] max-w-md px-3 py-1.5 text-textMuted">
          {c.reasoning}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={planning ? 7 : 6} className="bg-surface2 px-3 py-1.5">
            <ul className="space-y-1">
              {c.deliveries.map(d => (
                <li key={`${d.receipt_id}-${d.date}`}>
                  <span className="text-textMuted">{formatDate(d.date)}</span> · {d.qty}{' '}
                  {d.qty === 1 ? 'unit' : 'units'}
                  {d.retailer ? <span className="text-textFaint"> · {d.retailer}</span> : null}
                </li>
              ))}
            </ul>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}
